import type {
  FiltersResponse,
  LogsResponse,
  CurrentUserInfo,
  ProjectStandingGroup,
  ProjectStandingPageResult,
  ProjectStandingQuery,
  ProjectStandingTypeCount,
} from './types'

const base = '' // same-origin or proxied via Vite

export async function getFilters(): Promise<FiltersResponse> {
  const r = await fetch(`${base}/api/logs/filters`, { credentials: 'include' })
  if (!r.ok) throw new Error(`filters ${r.status}`)
  return r.json()
}

export interface LogsQuery { 
  startDate?: string
  endDate?: string
  level?: string
  application?: string
  operationType?: string
  serverName?: string
  maxResults?: number
  continuationToken?: string
}

export async function getLogs(q: LogsQuery): Promise<LogsResponse> {
  const p = new URLSearchParams()
  if (q.startDate) p.set('startDate', q.startDate)
  if (q.endDate) p.set('endDate', q.endDate)
  if (q.level) p.set('level', q.level)
  if (q.application) p.set('application', q.application)
  if (q.operationType) p.set('operationType', q.operationType)
  if (q.serverName) p.set('serverName', q.serverName)
  if (q.maxResults) p.set('maxResults', String(q.maxResults))
  if (q.continuationToken) p.set('continuationToken', q.continuationToken)

  const r = await fetch(`/api/logs?${p.toString()}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`logs ${r.status}`)
  return r.json()
}

export async function getProjectStandings(q: ProjectStandingQuery): Promise<ProjectStandingPageResult> {
  const p = new URLSearchParams()
  p.set('page', String(q.page))
  p.set('pageSize', String(q.pageSize))
  if (q.ownership) p.set('ownership', q.ownership)
  if (q.eligibilityStatus) p.set('eligibilityStatus', q.eligibilityStatus)
  if (q.standingStatus) p.set('standingStatus', q.standingStatus)
  if (q.sortBy) p.set('sortBy', q.sortBy)
  if (q.sortDirection) p.set('sortDirection', q.sortDirection)
  if (q.sinceYear) p.set('sinceYear', String(q.sinceYear))
  if (q.projectType) p.set('projectType', q.projectType)
  if (q.lifecycle) p.set('lifecycle', q.lifecycle)

  const r = await fetch(`/api/projectstanding/standings?${p.toString()}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`projectstanding ${r.status}`)
  return r.json()
}

export async function getProjectStandingGroups(): Promise<ProjectStandingGroup[]> {
  const r = await fetch('/api/projectstanding/groups', { credentials: 'include' })
  if (!r.ok) throw new Error(`projectstanding groups ${r.status}`)
  return r.json()
}

export async function getProjectStandingProjectTypes(): Promise<string[]> {
  const r = await fetch('/api/projectstanding/projecttypes', { credentials: 'include' })
  if (!r.ok) throw new Error(`projectstanding projecttypes ${r.status}`)
  return r.json()
}

export async function getProjectStandingTypeCounts(q: ProjectStandingQuery): Promise<ProjectStandingTypeCount[]> {
  const p = new URLSearchParams()
  if (q.ownership) p.set('ownership', q.ownership)
  if (q.eligibilityStatus) p.set('eligibilityStatus', q.eligibilityStatus)
  if (q.standingStatus) p.set('standingStatus', q.standingStatus)
  if (q.sortBy) p.set('sortBy', q.sortBy)
  if (q.sortDirection) p.set('sortDirection', q.sortDirection)
  if (q.sinceYear) p.set('sinceYear', String(q.sinceYear))
  if (q.projectType) p.set('projectType', q.projectType)
  if (q.lifecycle) p.set('lifecycle', q.lifecycle)

  const r = await fetch(`/api/projectstanding/typecounts?${p.toString()}`, { credentials: 'include' })
  if (!r.ok) throw new Error(`projectstanding typecounts ${r.status}`)
  return r.json()
}

export async function recalculateProjectStanding(projectId: number): Promise<void> {
  const r = await fetch(`/api/projectstanding/${projectId}/recalculate`, {
    method: 'POST',
    credentials: 'include',
  })

  if (!r.ok) throw new Error(`projectstanding recalculate ${r.status}`)
}

export async function setProjectStandingExclusion(
  projectId: number,
  isExcluded: boolean,
  excludeReason: string,
): Promise<void> {
  const r = await fetch(`/api/projectstanding/${projectId}/exclusion`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      IsExcluded: isExcluded,
      ExcludeReason: excludeReason,
    }),
  })

  if (!r.ok) throw new Error(`projectstanding exclusion ${r.status}`)
}

export async function getCurrentUserInfo(): Promise<CurrentUserInfo> {
  const r = await fetch('/api/currentuser/me', { credentials: 'include' })
  if (!r.ok) throw new Error(`currentuser ${r.status}`)
  return r.json()
}
